import React, {
  ChangeEvent,
  FormEvent,
  InputHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Button, Icon, Tooltip, mergeRefs } from '@column/column-ui-kit';

export type SearchProps = InputHTMLAttributes<HTMLInputElement> & {
  className?: string;
  value?: string;
  tooltipContent?: string;
  onValueChange?: (value: string) => void;
  onFilterSubmit?: (value: string) => void;
  isLoading?: boolean;
  showEnterHint?: boolean;
  width?: number;
};

const ENTER_HINT_INNER_PAD_PX = 32 + 36 + 1;
const EXPAND_PX = 22;

const Container = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
  padding-left: 1px;
`;

const Wrapper = styled.label<{ $isFilled?: boolean; $showEnter?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  transition:
    background-color 0.2s,
    box-shadow 0.2s,
    padding-right 0.2s;
  background-color: ${({ theme, $isFilled }) =>
    $isFilled
      ? theme.secondary.blendToBackground(theme.id === 'dark' ? 30 : 125)
      : theme.secondary.blendToBackground(theme.id === 'dark' ? 15 : 75)};
  box-shadow: 0 0 0 1px inset
    ${({ theme, $isFilled }) =>
      $isFilled
        ? theme.secondary.blendToBackground(theme.id === 'dark' ? 75 : 200)
        : theme.secondary.blendToBackground(theme.id === 'dark' ? 50 : 150)};
  border-radius: 8px;
  padding-right: ${({ $showEnter }) => ($showEnter ? 36 : 14)}px;
  padding-left: 32px;

  &:focus-within {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(theme.id === 'dark' ? 30 : 125)};
    box-shadow: 0 0 0 1px inset ${({ theme }) => theme.secondary.blendToBackground(theme.id === 'dark' ? 75 : 200)};
  }
`;

const Input = styled.input`
  appearance: none;
  outline: none;
  background-color: transparent;
  border: none;
  font-size: 14px;
  margin: 0;
  line-height: 16px;
  height: 16px;
  display: block;
  padding: 8px 0;
  width: 100%;
  flex-grow: 1;
  color: ${({ theme }) => theme.secondary.background};
  transition:
    color 0.2s,
    background-color 0.2s;
  width: auto;

  &::placeholder {
    opacity: 1;
    color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    transition: color 0.2s;
  }

  &:focus-visible,
  &:hover {
    &::placeholder {
      color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    }
  }
`;

const SearchIcon = styled(Icon.AnimationSearch)<{ $isActive?: boolean }>`
  transform-origin: 0 0;
  transform: scale(0.8) translateZ(0);
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  position: absolute;
  top: 8px;
  left: 8px;
  pointer-events: none;
  transition: color 0.2s;

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${({ theme }) => theme.primary.background};
    `}
`;

const Enter = styled(Button)<{ $isVisible?: boolean }>`
  --button-padding-x-xs: 6px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: translateX(${({ $isVisible }) => ($isVisible ? 0 : -4)}px) translateZ(0);
  transition:
    opacity 0.2s,
    transform 0.2s,
    background-color 0.2s,
    box-shadow 0.2s;
  cursor: pointer;
  position: absolute;
  width: 24px;
  height: 24px;
  line-height: 17.5px;
  right: 4px;
  top: 4px;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`;

export const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      value,
      onValueChange,
      onFilterSubmit,
      className,
      isLoading,
      placeholder,
      tooltipContent,
      showEnterHint,
      width,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const [currentValue, setCurrentValue] = useState<string>(value ?? '');
    const [localTooltipContent, setLocalTooltipContent] = useState<string | undefined>(tooltipContent);

    useLayoutEffect(() => {
      if (value !== currentValue) {
        setCurrentValue(value ?? '');
      }
    }, [value]);

    useEffect(() => {
      if (!tooltipContent || tooltipContent.length < 1 || tooltipContent === localTooltipContent) {
        return;
      }
      setLocalTooltipContent(tooltipContent);
    }, [tooltipContent]);

    useLayoutEffect(() => {
      const input = inputRef.current;
      const form = formRef.current;
      if (!input || !form) {
        return;
      }

      let span = form.querySelector<HTMLSpanElement>(':scope > .shadow-element');

      if (!span) {
        span = document.createElement('span');
        span.classList.add('shadow-element');
        span.style.font = getComputedStyle(input).font;
        span.style.fontSize = '14px';
        span.style.letterSpacing = getComputedStyle(input).letterSpacing;
        span.style.position = 'absolute';
        span.style.isolation = 'isolate';
        span.style.opacity = '0';
        span.style.pointerEvents = 'none';
        form.appendChild(span);
      }

      span.textContent = (currentValue || placeholder) ?? '';

      const contentWidth = span.offsetWidth + 2;
      input.style.width = `${contentWidth}px`;

      if (width !== undefined) {
        const tooltipRoot = form.parentElement;
        if (tooltipRoot instanceof HTMLElement) {
          const overflows = contentWidth > width - ENTER_HINT_INNER_PAD_PX;
          const isExpanded = Boolean(showEnterHint) && overflows;
          tooltipRoot.style.width = `${isExpanded ? width + EXPAND_PX : width}px`;
        }
      }
    }, [placeholder, currentValue, width, showEnterHint]);

    const handleSubmit = useCallback(
      (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (onFilterSubmit) {
          onFilterSubmit((event.target as HTMLFormElement).search?.value ?? '');
        }
      },
      [value, onFilterSubmit]
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (onValueChange) {
          onValueChange(event.target.value ?? '');
        }

        setCurrentValue(event.target.value ?? '');

        if (inputProps.onChange) {
          inputProps.onChange(event);
        }
      },
      [onValueChange, inputProps.onChange]
    );

    return (
      <Tooltip
        className={className}
        content={localTooltipContent}
        placement="top-start"
        offsetY={-4}
        isOpen={!!tooltipContent}
      >
        <Container ref={formRef} onSubmit={handleSubmit} autoComplete="off">
          <Wrapper $isFilled={!!currentValue && currentValue.length > 8} $showEnter={showEnterHint}>
            <SearchIcon running={isLoading} $isActive={isLoading} />
            <Input
              ref={mergeRefs([inputRef, ref])}
              {...inputProps}
              onChange={handleChange}
              value={value}
              placeholder={placeholder}
              name="search"
            />
            <Enter variant="secondary" size="tiny" type="submit" $isVisible={showEnterHint}>
              ↵
            </Enter>
          </Wrapper>
        </Container>
      </Tooltip>
    );
  }
);
